/**
 * ODUL MAGAZASI — API ile arayuzun paylastigi sozlesme (F-GAM-16, oyunlastirma dalga 2 surum 7a).
 *
 * Tasarim: dalga-2-tasarim.md §1.3, §3.1, §3.2 ve §10 EMRE KARARLARI (07.10.2026). Burada yalniz BICIM dogrulanir;
 * fiyat, stok, yas (18 alti/ustu), veli bagi, son gun ve durum gecisi SUNUCUDA, kisi kilidi altinda karara baglanir.
 * Istemci fiyat, puan, durum ya da ogrenci kimligi GONDEREMEZ (sema `strict`: fazla alan reddedilir).
 */
import { z } from "zod";
import { telefonNormalize, TELEFON_MESAJI } from "./telefon";

// --------------------------------------------------------------------------- sabitler

/** Siparis durumu — veritabani `OrderStatus` ile ayni. */
export const SIPARIS_DURUMLARI = [
  "AWAITING_PARENT",
  "AWAITING_ADDRESS",
  "PREPARING",
  "SHIPPED",
  "DELIVERED",
  "CANCELLED",
] as const;
export type SiparisDurumu = (typeof SIPARIS_DURUMLARI)[number];

/** Iptal gerekcesi — veritabani `OrderCancelReason` ile ayni (serbest metin yok). */
export const SIPARIS_IPTAL_NEDENLERI = [
  "STUDENT",
  "PARENT_REJECTED",
  "PARENT_TIMEOUT",
  "ADMIN",
  "UNDELIVERABLE",
] as const;
export type SiparisIptalNedeni = (typeof SIPARIS_IPTAL_NEDENLERI)[number];

export const SIPARIS_DURUMU_ETIKETI: Record<SiparisDurumu, string> = {
  AWAITING_PARENT: "Veli onayı bekleniyor",
  AWAITING_ADDRESS: "Adres bekleniyor",
  PREPARING: "Hazırlanıyor",
  SHIPPED: "Kargoda",
  DELIVERED: "Teslim edildi",
  CANCELLED: "İptal edildi",
};

export const SIPARIS_IPTAL_ETIKETI: Record<SiparisIptalNedeni, string> = {
  STUDENT: "Sen iptal ettin",
  PARENT_REJECTED: "Velin onaylamadı",
  PARENT_TIMEOUT: "Süresi içinde onaylanmadı",
  ADMIN: "Kurum iptal etti",
  UNDELIVERABLE: "Kargo teslim edilemedi",
};

/** Fiyat onericisinin kabul ettigi hedef ay araligi (veritabani CHECK ile ayni). */
export const HEDEF_AY_EN_AZ = 1;
export const HEDEF_AY_EN_COK = 24;

// --------------------------------------------------------------------------- yardimcilar

const istekKimligi = z.string().uuid();
const kimlik = z.string().trim().min(1).max(40);

/**
 * TL tutari: "1234.56" (en cok iki ondalik, en cok 99.999.999,99). Sayi DEGIL metin — para `Decimal` tutulur
 * (CLAUDE.md kural 8); JavaScript sayisina cevirmek 0,1 + 0,2 hatasini veritabanina tasirdi.
 */
export const tlSemasi = z
  .string()
  .trim()
  .regex(/^\d{1,8}(\.\d{1,2})?$/, "Tutarı 1234.56 biçiminde yazın (en çok iki ondalık).");

const metin = (enAz: number, enCok: number, alan: string) =>
  z
    .string()
    .trim()
    .min(enAz, `${alan} en az ${enAz} karakter olmalı.`)
    .max(enCok, `${alan} en çok ${enCok} karakter olabilir.`);

// --------------------------------------------------------------------------- urun (yonetim)

const urunAlanlari = {
  ad: metin(2, 120, "Ürün adı"),
  aciklama: z.string().trim().max(1000, "Açıklama en çok 1000 karakter olabilir.").nullish(),
  /** Puan fiyati — Emre girer (F-GAM-16). Fiyat onerici yalniz onerir. */
  puanFiyati: z.number().int().positive().max(10_000_000),
  stok: z.number().int().min(0).max(100_000),
  /** Ogrenci basina sinir; bos = sinirsiz (S12). */
  kisiBasiSinir: z.number().int().positive().max(1000).nullish(),
  maliyetTl: tlSemasi.nullish(),
  kargoTl: tlSemasi.nullish(),
  hedefAy: z.number().int().min(HEDEF_AY_EN_AZ).max(HEDEF_AY_EN_COK).nullish(),
  etkin: z.boolean().optional(),
  sira: z.number().int().min(-10_000).max(10_000).optional(),
};

export const urunIstegiSemasi = z.object(urunAlanlari).strict();
export type UrunIstegi = z.infer<typeof urunIstegiSemasi>;

export const urunGuncellemeSemasi = z
  .object(urunAlanlari)
  .partial()
  .strict()
  .refine((g) => Object.keys(g).length > 0, { message: "Değiştirilecek en az bir alan gönderin." });
export type UrunGuncelleme = z.infer<typeof urunGuncellemeSemasi>;

/** Fiyat onerisi sorgusu (`?ay=8&maliyetTl=...&kargoTl=...`) — sorgu dizgileri sayiya cevrilir. */
export const fiyatOnerisiSorguSemasi = z
  .object({
    ay: z.coerce.number().int().min(HEDEF_AY_EN_AZ).max(HEDEF_AY_EN_COK),
    maliyetTl: tlSemasi.optional(),
    kargoTl: tlSemasi.optional(),
  })
  .strict();
export type FiyatOnerisiSorgu = z.infer<typeof fiyatOnerisiSorguSemasi>;

// --------------------------------------------------------------------------- siparis (ogrenci)

/** Ogrenci siparisi — YALNIZ urun ve istek kimligi. Fiyat, puan, durum sunucuda. */
export const siparisIstegiSemasi = z
  .object({
    urunId: kimlik,
    istekKimligi,
  })
  .strict();
export type SiparisIstegi = z.infer<typeof siparisIstegiSemasi>;

/**
 * Teslim adresi (S4 yalniz kargo). Telefon E.164'e normalize edilir (`telefon.ts`). KISISEL VERI: denetim kaydina,
 * bildirime ve loga GIRMEZ; teslim/iptalden 6 ay sonra silinir (S12).
 */
export const adresSemasi = z
  .object({
    aliciAdi: metin(3, 100, "Alıcı adı"),
    telefon: z
      .string()
      .trim()
      .max(30)
      .transform((t, ctx) => {
        const s = telefonNormalize(t);
        if (s.gecerli) return s.e164;
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: TELEFON_MESAJI[s.neden] });
        return z.NEVER;
      }),
    il: metin(2, 60, "İl"),
    ilce: metin(2, 60, "İlçe"),
    adres: metin(10, 300, "Açık adres"),
    postaKodu: z
      .string()
      .trim()
      .regex(/^\d{5}$/, "Posta kodu 5 haneli olmalı.")
      .nullish(),
  })
  .strict();
export type SiparisAdresi = z.infer<typeof adresSemasi>;

/** 18+ ogrencinin kendi adresi ya da velinin onayla birlikte verdigi adres. */
export const adresIstegiSemasi = z.object({ adres: adresSemasi }).strict();
export type AdresIstegi = z.infer<typeof adresIstegiSemasi>;

// --------------------------------------------------------------------------- yonetim

/** Yonetici durum ilerletmesi: kargoya ver (firma + takip no) ya da teslim edildi. Iptal ayri uc. */
export const siparisDurumIstegiSemasi = z.discriminatedUnion("durum", [
  z
    .object({
      durum: z.literal("SHIPPED"),
      kargoFirmasi: metin(2, 60, "Kargo firması"),
      takipNo: z
        .string()
        .trim()
        .regex(/^[A-Za-z0-9-]{4,40}$/, "Takip numarası 4–40 harf/rakam olmalı."),
    })
    .strict(),
  z.object({ durum: z.literal("DELIVERED") }).strict(),
]);
export type SiparisDurumIstegi = z.infer<typeof siparisDurumIstegiSemasi>;

/** Kargodaki sipariste takip bilgisi duzeltmesi (yanlis girilen takip no). */
export const kargoDuzeltmeSemasi = z
  .object({
    kargoFirmasi: metin(2, 60, "Kargo firması"),
    takipNo: z
      .string()
      .trim()
      .regex(/^[A-Za-z0-9-]{4,40}$/, "Takip numarası 4–40 harf/rakam olmalı."),
  })
  .strict();
export type KargoDuzeltme = z.infer<typeof kargoDuzeltmeSemasi>;

/**
 * Yonetici iptali: hazirlanmadan once `ADMIN`, kargodan donen `UNDELIVERABLE` (S6). Puan AYNI islemde iade edilir,
 * stok doner. Serbest gerekce metni YOK (tasarim §1.3: iptal gerekcesi enum).
 */
export const yonetimIptalSemasi = z.object({ neden: z.enum(["ADMIN", "UNDELIVERABLE"]) }).strict();
export type YonetimIptal = z.infer<typeof yonetimIptalSemasi>;

/**
 * S11 — kagit veli onayi (cok parcali formun metin alanlari; `belge` dosya parcasi ayri). `adres` JSON metni olarak
 * gelir (form alani). `veliUserId` verilirse ogrenciyle GERCEK veli bagi aranir (portal erisimi sart degil — yol
 * tam da portala erisemeyen veli icin).
 */
export const kagitOnaySemasi = z
  .object({
    adres: adresSemasi,
    veliUserId: kimlik.nullish(),
  })
  .strict();
export type KagitOnay = z.infer<typeof kagitOnaySemasi>;

export const yonetimSiparisListeSemasi = z
  .object({
    durum: z.enum(SIPARIS_DURUMLARI).optional(),
    ogrenciId: kimlik.optional(),
    imlec: kimlik.optional(),
  })
  .strict();
export type YonetimSiparisListe = z.infer<typeof yonetimSiparisListeSemasi>;

// --------------------------------------------------------------------------- yanitlar

/** Vitrin satiri — maliyet YOK. Stok sayisi gosterilmez; yalniz "tukendi" (S7). */
export interface UrunVitrinOzeti {
  id: string;
  ad: string;
  aciklama: string | null;
  puanFiyati: number;
  tukendi: boolean;
  /** Ogrenci basina sinir; null = sinirsiz */
  kisiBasiSinir: number | null;
  /** Sinir varsa bu ogrencinin kalan hakki (iptal edilmemis siparisler sayilir) */
  kalanHak: number | null;
}

/**
 * Emre 07.10.2026 (7b kararlari, madde 7): magazada YALNIZ `ACTIVE` ogrenci YENI siparis verir. Diger hesap
 * durumlarinda siparis istegi 409 `SIPARIS_HESAP_DURUMU` ({kod, mesaj, durum}) doner; vitrin ayni nedeni
 * `siparisVerilemez` alaninda onceden soyler. Mesaj SUNUCUDAN (ekran kendi cumlesini uretmez). Mevcut siparisin
 * iptali, veli onayi/reddi, kargo/teslim ve zaman asimi iptali bu kurala TABI DEGIL.
 */
export const SIPARIS_HESAP_DURUMU = "SIPARIS_HESAP_DURUMU" as const;

export interface SiparisVerilemez {
  kod: typeof SIPARIS_HESAP_DURUMU;
  /** Ogrencinin hesap durumu (StudentStatus: FROZEN, EXPIRED, ...). */
  durum: string;
  /** Ogrenciye gosterilecek Turkce aciklama. */
  mesaj: string;
}

/** Vitrin yaniti: bakiye sunucudan (ekran toplamaz); personel icin 0. */
export interface MagazaVitrini {
  bakiye: number;
  urunler: UrunVitrinOzeti[];
  /**
   * Ogrenci su an YENI siparis veremiyorsa nedeni (hesap durumu); `ACTIVE` ise null. Ogrenci kaydi olmayan
   * (personel) icin de null — vitrini yalniz gorur, siparis ucu zaten 404 doner.
   */
  siparisVerilemez: SiparisVerilemez | null;
}

export interface UrunYonetimOzeti {
  id: string;
  ad: string;
  aciklama: string | null;
  puanFiyati: number;
  stok: number;
  kisiBasiSinir: number | null;
  /** TL, "1234.56" — Decimal'den metin */
  maliyetTl: string | null;
  kargoTl: string | null;
  hedefAy: number | null;
  etkin: boolean;
  sira: number;
  arsivlendi: string | null;
  /** Iptal edilmemis siparis sayisi */
  siparisSayisi: number;
}

export interface SiparisOzeti {
  id: string;
  urunId: string;
  urunAdi: string;
  puan: number;
  durum: SiparisDurumu;
  /** Veli onayi / adres son gunu (ISO) — yalniz bekleyen sipariste */
  sonGun: string | null;
  kagitOnayli: boolean;
  kargoFirmasi: string | null;
  takipNo: string | null;
  kargoTarihi: string | null;
  teslimTarihi: string | null;
  iptalTarihi: string | null;
  iptalNedeni: SiparisIptalNedeni | null;
  /** Ogrenci iptal edebilir mi (kargoya verilene kadar, S5) */
  iptalEdilebilir: boolean;
  /** 18+ ogrenci kendi adresini girebilir mi */
  adresBekleniyor: boolean;
  /** Teslim adresi verildi mi (adresin KENDISI donmez). Hazirlaniyor/kargoda/teslimde tetikleyici geregi dolu. */
  adresVerildi: boolean;
  tarih: string;
}

export interface SiparisYonetimOzeti extends SiparisOzeti {
  ogrenciId: string;
  ogrenciAdi: string;
  /** S11: veli onayi bekleniyor ve ogrencinin portal erisimli velisi YOK — yonetici kagit onayi verebilir. */
  kagitOnayiGerekli: boolean;
}

/** Yonetim detay — adres YALNIZ burada (`oyun.siparis.yonet`). Silinmisse `adres: null`, `adresSilindi` dolu. */
export interface SiparisYonetimDetayi extends SiparisYonetimOzeti {
  adres: {
    aliciAdi: string;
    telefon: string;
    il: string;
    ilce: string;
    adres: string;
    postaKodu: string | null;
  } | null;
  adresSilindi: string | null;
  /** Adresi veren: veli, ogrenci ya da kagit onayinda yonetici */
  adresiVeren: "VELI" | "OGRENCI" | "YONETICI" | null;
  kagitOnayTarihi: string | null;
  kagitKanitSilindi: string | null;
}

/**
 * Veliye gorunen onay karti (S12): cocugun adi, urun adi, son gun. PUAN, BAKIYE, FIYAT YOK — veli oyunlastirmayi
 * gormez (bekci testi yanit tipinde bu alanlarin yokluguna bakar).
 */
export interface VeliSiparisOnayi {
  id: string;
  /** Cocugun ogrenci kimligi — veli zaten bagli cocuklarini biliyor; kart cocuk bazli sayim icin. */
  ogrenciId: string;
  cocukAdi: string;
  urunAdi: string;
  sonGun: string;
}

/** Veli karari yaniti. */
export interface VeliKararYaniti {
  id: string;
  onaylandi?: true;
  reddedildi?: true;
}

/** Fiyat onerisi (tasarim §3.2). Onerilen fiyat siparise degil yalniz ekrana gider. */
export interface FiyatOnerisi {
  /** 50'ye yuvarli puan */
  onerilen: number;
  /** "VERI" = kesinlesmis haftalardan; "KURAL" = az veri, kural surumunden duzenli ogrenci profili */
  yontem: "VERI" | "KURAL";
  /** Ogrenci-hafta basina harcanabilir kazanim (medyan / ust ceyrek) */
  haftalikMedyan: number;
  haftalikUstCeyrek: number;
  /** En hizli ceyrek bu fiyata kac ayda ulasir (ust ceyrek hiziyla) */
  enHizliAy: number | null;
  ogrenciHafta: number;
  kesinHafta: number;
  /** Urun + kargo (S2) — TL metin */
  toplamMaliyetTl: string | null;
  uyari: string | null;
}
